import { useCallback, useEffect, useRef, useState } from 'react';

import GetEventRegistrations from '@/connection/analytics/get-event-registrations';
import GetEventAttendanceRate from '@/connection/analytics/get-event-attendance-rate';
import GetEventUniqueAttendance from '@/connection/analytics/get-event-unique-attendance';
import GetEventAverageUserWatchtime from '@/connection/analytics/get-event-average-user-watchtime';
import { IAnalyticsScorecard } from '@/types/analytics';
import GetLiveAndOnDemandEventViews from '@/connection/analytics/project-level/get-live-and-on-demand-event-views';
import { useAppSelector } from '@/store/hooks';
import { stageEnvironment } from '@/utils/urls';

export type TStats = {
  avgUserWatchtime: IAnalyticsScorecard | null;
  uniqueAttendees: IAnalyticsScorecard | null;
  registrations: IAnalyticsScorecard | null;
  attendanceRate: Pick<IAnalyticsScorecard, 'value'> | null;
  projectViews: IAnalyticsScorecard | null;
};

type TFetchSessionStatsProps = {
  projectUuid?: string;
  sessionUuid?: string;
  language?: string;
  disabled?: boolean;
};

const useFetchSessionStats = ({ projectUuid, sessionUuid, language, disabled }: TFetchSessionStatsProps) => {
  const fetching = useRef(false);

  const createdDate = useAppSelector((state) => state.project.workingProject?.created_date);

  const abortControllersRef = useRef({
    avgUserWatchtimesController: new AbortController(),
    uniqueAttendeesController: new AbortController(),
    registrationsController: new AbortController(),
    attendanceRateController: new AbortController(),
    eventViewsController: new AbortController(),
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TStats>({
    avgUserWatchtime: null,
    uniqueAttendees: null,
    registrations: null,
    attendanceRate: null,
    projectViews: null,
  });

  const abort = useCallback(() => {
    const controllers = abortControllersRef.current;
    Object.values(controllers).forEach((controller) => controller.abort());
  }, []);

  useEffect(() => {
    if (disabled) return;
    if (!sessionUuid) return;
    if (!projectUuid) return;
    if (fetching.current) return;

    (async () => {
      try {
        fetching.current = true;
        setLoading(true);
        const controllers = abortControllersRef.current;

        const start = new Date(createdDate || 0).valueOf();

        // TODO:
        // these api calls are not the correct ones for the event overview stats
        // need to be updated, however I don't believe we currently have actual queries for the stats in figma

        const results = await Promise.allSettled([
          GetEventAverageUserWatchtime({ projectUuid, start, signal: controllers.avgUserWatchtimesController.signal }),
          GetEventUniqueAttendance({ projectUuid, start, signal: controllers.uniqueAttendeesController.signal }),
          GetEventRegistrations({
            projectUuid,
            start,
            end: Date.now(),
            signal: controllers.registrationsController.signal,
          }),
          GetEventAttendanceRate({ projectUuid, signal: controllers.attendanceRateController.signal }),
          GetLiveAndOnDemandEventViews({ projectUuid, start, signal: controllers.eventViewsController.signal }),
        ]);

        const avgUserWatchtime = results[0].status === 'fulfilled' ? results[0].value : null;
        const uniqueAttendees = results[1].status === 'fulfilled' ? results[1].value : null;
        const registrations = results[2].status === 'fulfilled' ? results[2].value : null;
        const attendanceRate = results[3].status === 'fulfilled' ? results[3].value : null;
        const projectViews = results[4].status === 'fulfilled' ? results[4].value : null;

        setData({
          avgUserWatchtime,
          uniqueAttendees,
          registrations,
          attendanceRate,
          projectViews,
        });
      } catch (error) {
        console.error('Failed to fetch project stats:', error);
      } finally {
        fetching.current = false;
        setLoading(false);
      }
    })();

    return () => {
      // Note: on localhost (development mode), this cleanup always triggers because React.StrictMode forces hooks to trigger twice.
      // So the data on localhost is always null, therefore I'm not calling it on localhost.
      // It does not do so in remote environments.
      if (!stageEnvironment.isLocal) {
        abort();
      }
    };
  }, [language, projectUuid, createdDate, abort, disabled, sessionUuid]);

  return { loading, data, abort };
};

export default useFetchSessionStats;
